import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined'
import { BrandLogo } from '../components/BrandLogo'
import { useLogin } from '../auth/useLogin'
import { useAuthStore } from '../auth/authStore'
import { errorMessage } from '../api/client'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useLogin()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated())
  const user = useAuthStore((state) => state.user)
  const clear = useAuthStore((state) => state.clear)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(
    (location.state as { reason?: string } | null)?.reason === 'not-admin'
      ? 'Este panel es exclusivo para administradores. Los empleados registran su asistencia desde la aplicación móvil.'
      : null,
  )

  if (isAuthenticated && user?.role === 'admin') {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from ?? '/'} replace />
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    try {
      const result = await login.mutateAsync({ email, password })

      // El backend autentica a cualquier usuario válido; el panel es solo
      // para administradores, así que la sesión de un empleado se descarta.
      if (result.user.role !== 'admin') {
        clear()
        setError(
          'Este panel es exclusivo para administradores. Los empleados registran su asistencia desde la aplicación móvil.',
        )
        return
      }

      navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true })
    } catch (caught) {
      setError(errorMessage(caught, 'No se pudo iniciar sesión.'))
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }} elevation={0} variant="outlined">
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={1.5} sx={{ alignItems: 'center', mb: 3 }}>
            <BrandLogo width={260} />
            <Typography variant="body2" color="text.secondary">
              Control de asistencia · Panel de administración
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                required
                fullWidth
                size="medium"
              />

              <TextField
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                fullWidth
                size="medium"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((visible) => !visible)}
                          edge="end"
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                loading={login.isPending}
              >
                Ingresar
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
